/**
 * Maps a pending HITL task row (Platform `POST /v1/tasks/hitl/list`) into the raw
 * Work Queue event shape consumed by {@link mapApiEventToUi}.
 *
 * The HITL API stores an opaque `render` blob authored by LangGraph. Work Queues
 * reads an optional explicit `render.work_queue_event_type` (our curl/dev seeds set
 * this); when absent, falls back to `render.ui_block` heuristics shared with chat.
 */

import { CATEGORY, EVENT_TYPE } from '../constants/eventTypes.js';
import { SEVERITY } from '../constants/severities.js';
import { DUE_BUCKET } from '../constants/filterOptions.js';
import { applyHandoffDetailFixture } from '../constants/handoffFixtures.js';
import { applyBreakGlassDetailFixture } from '../constants/breakGlassFixtures.js';
import { resolveIsCustomer } from './customerClassification.js';

const UI_BLOCK_TO_EVENT_TYPE = {
  hitl_approval: EVENT_TYPE.HTL_CONTINUATION,
  hitl_handoff: EVENT_TYPE.HTL_HANDOFF,
  hitl_qa: EVENT_TYPE.QUESTION,
  hitl_break_glass: EVENT_TYPE.GOV_BREAK_GLASS,
};

const EVENT_CATEGORY = {
  [EVENT_TYPE.HTL_CONTINUATION]: CATEGORY.AGNT,
  [EVENT_TYPE.HTL_HANDOFF]: CATEGORY.AGNT,
  [EVENT_TYPE.CLIENT_CONTINUATION]: CATEGORY.CLIENT,
  [EVENT_TYPE.CLIENT_HANDOFF]: CATEGORY.CLIENT,
  [EVENT_TYPE.QUESTION]: CATEGORY.GOV,
  [EVENT_TYPE.GOV_BREAK_GLASS]: CATEGORY.GOV,
};

const ACTION_LABEL = {
  [EVENT_TYPE.HTL_CONTINUATION]: 'Approve',
  [EVENT_TYPE.CLIENT_CONTINUATION]: 'Approve',
  [EVENT_TYPE.HTL_HANDOFF]: 'Acknowledge',
  [EVENT_TYPE.CLIENT_HANDOFF]: 'Acknowledge',
  [EVENT_TYPE.QUESTION]: 'Reply',
  [EVENT_TYPE.GOV_BREAK_GLASS]: 'Approve',
};

const REF_LABEL = {
  [EVENT_TYPE.HTL_CONTINUATION]: 'HTL Continuation',
  [EVENT_TYPE.CLIENT_CONTINUATION]: 'Client Continuation',
  [EVENT_TYPE.HTL_HANDOFF]: 'HTL Handoff',
  [EVENT_TYPE.CLIENT_HANDOFF]: 'Client Handoff',
  [EVENT_TYPE.QUESTION]: 'Question',
  [EVENT_TYPE.GOV_BREAK_GLASS]: 'Break Glass',
};

const BROADCAST_QUESTION_LABEL = 'Broadcast Question';

/** Resolve prototype event type from the opaque render payload. */
export function resolveHitlEventType(render = {}) {
  const explicit =
    render.work_queue_event_type ??
    render.workQueueEventType ??
    render.event_type ??
    render.eventType;
  if (explicit && Object.values(EVENT_TYPE).includes(explicit)) return explicit;
  const uiBlock = render.ui_block ?? render.uiBlock;
  return UI_BLOCK_TO_EVENT_TYPE[uiBlock] ?? EVENT_TYPE.HTL_CONTINUATION;
}

function shortRef(contractId) {
  if (!contractId) return null;
  const tail = String(contractId).split(':').pop() ?? contractId;
  return tail.slice(-8).toUpperCase();
}

function initialsFromName(name = '') {
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** LangGraph may nest question text under `render.question.{text,from}` — UI expects strings. */
function normalizeQuestionFields(render = {}) {
  const variant =
    render.question_variant ??
    render.questionVariant ??
    (render.broadcast || render.message_style === 'broadcast' ? 'broadcast' : 'direct');
  const messageId = render.message_id ?? render.messageId ?? null;
  const askedAt = render.asked_at ?? render.askedAt ?? null;

  const raw = render.question;
  if (typeof raw === 'string') {
    const explicitMessageBody = render.message_body ?? render.messageBody ?? null;
    const messageBody =
      explicitMessageBody ??
      (variant === 'broadcast' ? raw : render.summary ?? render.context ?? null);
    const askedBy =
      render.askedBy ??
      render.asked_by ??
      (render.question_from
        ? {
            id: render.question_from,
            name: render.question_from,
            role: render.question_role ?? '',
            initials: initialsFromName(render.question_from),
          }
        : null);
    return {
      questionVariant: variant,
      messageId,
      askedAt,
      question: raw,
      messageBody,
      askedBy,
      context: render.context ?? (typeof render.summary === 'string' ? render.summary : null),
      linkedEvent: render.linkedEvent ?? render.linked_event ?? null,
    };
  }
  if (raw && typeof raw === 'object') {
    const name = raw.from ?? raw.name ?? 'Unknown';
    const prompt = raw.text ?? raw.question ?? render.summary ?? '';
    const explicitMessageBody = render.message_body ?? render.messageBody ?? null;
    const messageBody =
      explicitMessageBody ??
      (variant === 'broadcast' ? render.summary ?? render.context ?? prompt : null);
    return {
      questionVariant: variant,
      messageId,
      askedAt,
      question: prompt,
      messageBody,
      askedBy: {
        id: raw.id ?? raw.from ?? name,
        name,
        role: raw.role ?? '',
        initials: raw.initials ?? initialsFromName(name),
      },
      context: render.context ?? null,
      linkedEvent: render.linkedEvent ?? render.linked_event ?? null,
    };
  }
  const fallbackBody = render.summary ?? render.title ?? '';
  return {
    questionVariant: variant,
    messageId,
    askedAt,
    question: fallbackBody,
    messageBody: render.message_body ?? render.messageBody ?? fallbackBody,
    context: render.context ?? null,
  };
}

function normalizeHandoffPacket(raw = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const keyFacts = raw.key_facts ?? raw.keyFacts ?? raw.findings ?? [];
  return {
    keyFacts: Array.isArray(keyFacts) ? keyFacts : [],
    findings: Array.isArray(keyFacts) ? keyFacts : [],
    recommendations: raw.recommendations ?? [],
    crmRecord: raw.crm_record ?? raw.crmRecord ?? null,
    knowledgeContract: raw.knowledge_contract ?? raw.knowledgeContract ?? null,
    agentSummary: raw.agent_summary ?? raw.agentSummary ?? null,
    nextAction: raw.next_action ?? raw.nextAction ?? null,
    linkedRecords: raw.linked_records ?? raw.linkedRecords ?? [],
  };
}

function normalizeHandoffFields(render = {}) {
  const handoff = render.handoff ?? {};
  const handoffObj = typeof handoff === 'object' && handoff !== null ? handoff : {};
  const rawPacket = render.packet ?? handoffObj.packet ?? null;
  const packet = normalizeHandoffPacket(rawPacket);
  const entity =
    handoffObj.entity ??
    render.entity ??
    handoffObj.entityContext ??
    handoffObj.entity_context ??
    render.entity_context ??
    null;
  const handoffJustification =
    handoffObj.justification ??
    handoffObj.reason ??
    (typeof handoff === 'string' ? handoff : null) ??
    null;

  return {
    workflow: render.workflow ?? handoffObj.workflow ?? null,
    entity,
    recordId:
      render.record_id ??
      render.recordId ??
      handoffObj.record_id ??
      handoffObj.recordId ??
      render.ref_id ??
      render.refId ??
      null,
    source: render.source ?? handoffObj.source ?? null,
    handoffJustification,
    lastAction: render.last_action ?? render.lastAction ?? handoffObj.last_action ?? null,
    handoffReason: handoffJustification,
    entityContext: entity,
    agentSummary:
      render.agent_summary ??
      render.agentSummary ??
      packet?.agentSummary ??
      null,
    packet,
    trace: render.trace ?? handoffObj.trace ?? null,
    traceRef: render.trace_ref ?? render.traceRef ?? handoffObj.trace_ref ?? null,
  };
}

function normalizeBreakGlassFields(render = {}) {
  const bg = render.break_glass ?? render.breakGlass ?? {};
  const requesterRaw = render.requester ?? bg.requester ?? null;
  const requester =
    typeof requesterRaw === 'object' && requesterRaw !== null
      ? {
          name: requesterRaw.name ?? 'Unknown',
          role: requesterRaw.role ?? '',
          initials:
            requesterRaw.initials ??
            String(requesterRaw.name ?? '')
              .split(/\s+/)
              .filter(Boolean)
              .map((part) => part[0])
              .join('')
              .slice(0, 2)
              .toUpperCase(),
        }
      : null;

  return {
    reason: bg.reason ?? render.reason ?? render.summary ?? null,
    requestedAt:
      render.requested_at ??
      render.requestedAt ??
      bg.requested_at ??
      bg.requestedAt ??
      null,
    requiredCount: bg.required_approvals ?? bg.requiredApprovals ?? bg.requiredCount ?? 2,
    approvers: render.approvers ?? bg.approvers ?? [],
    partition: bg.partition ?? render.partition ?? null,
    classification: bg.classification ?? render.classification ?? null,
    accessScope: bg.access_scope ?? bg.accessScope ?? render.access_scope ?? null,
    duration: bg.duration ?? render.duration ?? null,
    incidentRef: bg.incident_ref ?? bg.incidentRef ?? render.incident_ref ?? render.ref_id ?? null,
    security: render.security ?? bg.security ?? null,
    requester,
  };
}

function normalizeContinuationFields(render = {}) {
  return {
    workflow: render.workflow ?? null,
    confidence: render.confidence ?? render.confidence_score ?? null,
    geClassification: render.ge_classification ?? render.geClassification ?? null,
    output: render.output ?? null,
    trace: render.trace ?? null,
    traceRef: render.trace_ref ?? render.traceRef ?? null,
  };
}

function buildTypeDetail(type, render) {
  switch (type) {
    case EVENT_TYPE.QUESTION:
      return normalizeQuestionFields(render);
    case EVENT_TYPE.HTL_HANDOFF:
    case EVENT_TYPE.CLIENT_HANDOFF:
      return normalizeHandoffFields(render);
    case EVENT_TYPE.GOV_BREAK_GLASS:
      return normalizeBreakGlassFields(render);
    default:
      return normalizeContinuationFields(render);
  }
}

/** No date on the row: no label, no timestamp, `no_date` bucket. */
const NO_DUE = { dueLabel: null, dueBucket: DUE_BUCKET.NO_DATE, dueAt: null, overdue: false };

/**
 * Bucket + label + raw ISO timestamp from a HITL `expiresAt`. `dueAt` is what
 * the due-date sort orders by — the label is localized display text and sorting
 * on it compared English literals alphabetically.
 */
function dueFromExpires(expiresAt) {
  if (!expiresAt) {
    return { ...NO_DUE };
  }
  const expires = new Date(expiresAt);
  if (Number.isNaN(expires.getTime())) {
    return { ...NO_DUE };
  }
  const dueAt = expires.toISOString();
  const now = Date.now();
  if (expires.getTime() < now) {
    return { dueLabel: 'Expired', dueBucket: DUE_BUCKET.OVERDUE, dueAt, overdue: true };
  }
  const days = (expires.getTime() - now) / (24 * 60 * 60 * 1000);
  if (days <= 1) {
    return { dueLabel: 'Due today', dueBucket: DUE_BUCKET.DUE_TODAY, dueAt, overdue: false };
  }
  if (days <= 7) {
    return { dueLabel: 'This week', dueBucket: DUE_BUCKET.THIS_WEEK, dueAt, overdue: false };
  }
  // A date more than a week out still IS a date: it belongs in `later`. The old
  // `NO_DATE` fallback showed the row's date on the card while hiding it from
  // every date filter and listing it under "No date".
  return { dueLabel: expires.toLocaleDateString(), dueBucket: DUE_BUCKET.LATER, dueAt, overdue: false };
}

/**
 * @param {Object} task - Row from `POST /v1/tasks/hitl/list` (camelCase on wire).
 * @param {'my_work'|'my_team'} scope
 * @param {number} [index=0]
 * @returns {Object|null} Raw event for {@link mapApiEventToUi}, or null when not pending.
 */
export function mapHitlTaskToEvent(task = {}, scope, index = 0) {
  const status = String(task.status ?? 'pending').toLowerCase();
  if (status !== 'pending') return null;

  const render = task.render ?? {};
  const type = resolveHitlEventType(render);
  const contractId = task.contractId ?? task.contract_id ?? null;
  const taskUuid = task.taskUuid ?? task.task_uuid ?? null;
  const id = contractId ?? taskUuid ?? `hitl-${index}`;
  const title = render.title ?? render.feature_title ?? render.featureTitle ?? 'HITL task';
  const summary = render.summary ?? render.description ?? '';
  let typeDetail = buildTypeDetail(type, render);
  if (type === EVENT_TYPE.HTL_HANDOFF || type === EVENT_TYPE.CLIENT_HANDOFF) {
    typeDetail = applyHandoffDetailFixture(contractId, typeDetail);
  }
  if (type === EVENT_TYPE.GOV_BREAK_GLASS) {
    typeDetail = applyBreakGlassDetailFixture(contractId, typeDetail);
  }
  const category = EVENT_CATEGORY[type] ?? CATEGORY.AGNT;
  const isBreakGlass = type === EVENT_TYPE.GOV_BREAK_GLASS;
  const isContinuation = type === EVENT_TYPE.HTL_CONTINUATION || type === EVENT_TYPE.CLIENT_CONTINUATION;
  const isQuestion = type === EVENT_TYPE.QUESTION;
  const questionRefLabel =
    typeDetail.questionVariant === 'broadcast' ? BROADCAST_QUESTION_LABEL : REF_LABEL[type];

  return {
    id,
    scope,
    type,
    category,
    severity: isBreakGlass || isContinuation ? SEVERITY.CRITICAL : SEVERITY.ACTION,
    title,
    description: summary,
    refId:
      typeDetail.messageId ??
      ((type === EVENT_TYPE.HTL_HANDOFF || type === EVENT_TYPE.CLIENT_HANDOFF) &&
      typeDetail.recordId
        ? typeDetail.recordId
        : null) ??
      (type === EVENT_TYPE.GOV_BREAK_GLASS && render.ref_id
        ? render.ref_id
        : null) ??
      shortRef(contractId),
    refLabel: questionRefLabel ?? 'HITL',
    actionLabel: ACTION_LABEL[type] ?? 'Review',
    studio: render.studio ?? 'agentic-oversight',
    studioExtraCount: 0,
    status: isQuestion ? 'awaiting_reply' : 'paused',
    ...dueFromExpires(task.expiresAt ?? task.expires_at),
    isCustomer: resolveIsCustomer({
      type,
      render,
      category,
      title,
      description: summary,
      situation: summary,
      refLabel: questionRefLabel ?? REF_LABEL[type] ?? 'HITL',
      destination: render.destination,
      geClassification: render.ge_classification ?? render.geClassification,
      channel: typeDetail.workflow ?? render.channel,
    }),
    missionCritical: isBreakGlass || isContinuation,
    blocks: { workflows: 0, agents: 0 },
    commentsCount: 0,
    situation: summary,
    pipeline: [],
    detail: {
      source: 'hitl',
      contractId,
      taskUuid,
      hitlType: task.hitlType ?? task.hitl_type ?? null,
      nodeId: task.nodeId ?? task.node_id ?? null,
      render,
      expiresAt: task.expiresAt ?? task.expires_at ?? null,
      createdAt: task.createdAt ?? task.created_at ?? null,
      ...typeDetail,
    },
  };
}
